'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import ButtonLink from '@/ui/components/shared/ButtonLink';
import CopyIcon from '@/ui/components/shared/icons/copy-icon';

type StyleVariant = 'black' | 'grey' | 'white';

const VARIANT_STYLES: Record<StyleVariant, { background: string; text: string; accent: string }> = {
  black: { background: '#000000', text: '#FFFFFF', accent: '#FECB3F' },
  grey: { background: '#F2F2F2', text: '#000000', accent: '#FECB3F' },
  white: { background: '#FFFFFF', text: '#000000', accent: '#FECB3F' },
};

type PromotionBlockProps = {
  components: Record<string, any>;
  onPromoVoucherCopy?: () => void;
  onPromoLinkClick?: () => void;
};

export default function PromotionBlock({
  components,
  onPromoVoucherCopy,
  onPromoLinkClick,
}: Readonly<PromotionBlockProps>) {
  const t = useTranslations('PromotionBlock');
  const [copied, setCopied] = useState(false);

  const topLabel = components['top-label']?.text;
  const headline = components.headline?.text;
  const voucherCode = components['voucher-code']?.text;
  const richText = components.text;
  const footnote = components.footnote?.text;
  const ctaPiece = components.cta;
  const hasCta = ctaPiece?.components?.length > 0;

  const variant: StyleVariant = components.style?.options?.[0]?.key || 'black';
  const colors = VARIANT_STYLES[variant] || VARIANT_STYLES.black;

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    if (onPromoVoucherCopy) {
      onPromoVoucherCopy();
    }
  };

  if (!voucherCode && !hasCta) {
    return null;
  }

  return (
    <div
      className="w-full rounded-xl px-6 py-5 text-center"
      style={{ backgroundColor: colors.background, color: colors.text }}
    >
      {topLabel && (
        <p
          className="text-[14px] font-bold uppercase tracking-[0.56px]"
          style={{ color: colors.accent }}
        >
          {topLabel}
        </p>
      )}

      {headline && <p className="mt-1 text-[20px] font-bold leading-[1.3]">{headline}</p>}

      {richText?.json && (
        <div className="mt-2 text-[14px] leading-[1.4] opacity-80">
          <RichTextRenderer blocks={richText} />
        </div>
      )}

      {voucherCode && (
        <CopyToClipboard text={voucherCode} onCopy={handleCopy}>
          <button
            type="button"
            className="relative mx-auto mt-3 flex w-full max-w-sm items-center justify-center rounded-[10px] border border-dashed px-3 py-4 text-[14px] font-bold tracking-widest transition-opacity hover:opacity-80"
            style={{
              borderColor: colors.accent,
              color: colors.accent,
              backgroundColor: 'rgba(255,255,255,0.15)',
            }}
            aria-label={`${t('copy')} ${voucherCode}`}
          >
            {copied ? t('copied') : voucherCode.toUpperCase()}
            <CopyIcon className="absolute right-3 shrink-0" />
          </button>
        </CopyToClipboard>
      )}

      {!voucherCode && hasCta && (
        <div className="mt-4">
          <ButtonLink
            ctaComponent={ctaPiece}
            className="w-full rounded-[10px]"
            onClick={() => {
              if (onPromoLinkClick) {
                onPromoLinkClick();
              }
            }}
          />
        </div>
      )}

      {footnote && (
        <p className="mt-3 text-center text-[12px] leading-[1.2] opacity-70">{footnote}</p>
      )}
    </div>
  );
}
